<template>
  <div>
    <!-- MINIMIZED BUTTON -->
    <div
      v-if="isMinimized && hasFiles"
      class="upload-fab shadow"
      @click="toggleMinimize"
    >
      <i class="bx bx-cloud-upload"></i>
      <span v-if="uploading.length" class="badge bg-primary font-size-12">
        {{ uploading.length }}
      </span>
      <span v-else-if="completed.length" class="badge bg-success font-size-12">
        {{ completed.length }}
      </span>
      <span v-else-if="failed.length" class="badge bg-danger font-size-12">
        {{ failed.length }}
      </span>
    </div>

    <!-- FULL PANEL -->
    <transition name="slide-up">
      <div
        v-if="!isMinimized && hasFiles && isOpen"
        class="upload-panel card shadow-lg"
      >
        <!-- HEADER -->
        <div class="card-header bg-light d-flex justify-content-between align-items-center">
          <strong>
          <i class="mdi mdi-robot-outline me-1"></i>
            Smart Upload
          </strong>

          <div class="d-flex align-items-center">
            <i
              class="bx bx-minus font-size-16 me-2"
              style="cursor:pointer"
              @click="toggleMinimize"
            ></i>

            <!-- <i
              class="bx bx-x font-size-16 text-danger"
              style="cursor:pointer"
              @click="closePanel"
            ></i> -->
          </div>
        </div>

        <div class="card-body p-0 upload-body">
          <div class="upload-scroll p-2">

            <!-- COMPLETED SECTION -->
            <div v-if="completed.length" :class="{ 'mt-2': uploading.length }">
              <div class="section-title text-success small mb-2">
                <i class="mdi mdi-check-circle me-1"></i> Done
              </div>

              <div
                v-for="file in completed"
                :key="file.fileId"
                class="upload-item d-flex justify-content-between align-items-center"
              >
                <span class="text-truncate">
                  <i :class="`mdi font-size-14 me-1 ${getFileIcon(file.fileName)}`"></i>
                  {{ file.fileName }}
                </span>

                <div class="d-flex align-items-center gap-2">
                  <!-- <span class="badge bg-success">Done</span> -->
                  <b-button
                    size="sm"
                    variant="primary"
                    @click="review(file)"
                    class="text-white"
                  >
                    Review
                  </b-button>
                </div>
              </div>
            </div>

            <!-- UPLOADING SECTION -->
            <div v-if="uploading.length">
              <div class="section-title text-muted small mb-2">
                Uploading...
              </div>

              <div
                v-for="file in uploading"
                :key="file.fileId"
                class="upload-item"
              >
                <div class="d-flex justify-content-between">
                  <span class="text-truncate">
                    <i :class="`mdi font-size-14 me-1 ${getFileIcon(file.fileName)}`"></i>
                    {{ file.fileName }}
                  </span>
                  <small>
                    {{ file.status === 'processing' ? 'Processing...' : file.progress + '%' }}
                  </small>
                </div>

                <b-progress
                  :value="file.status === 'processing' ? 100 : file.progress"
                  height="6px"
                  class="mt-1"
                  :variant="file.status === 'processing' ? 'warning' : 'primary'"
                  animated
                />
              </div>
            </div>

            <!-- FAILED SECTION -->
            <div v-if="failed.length" :class="{ 'mt-2': uploading.length || completed.length }">
              <div class="section-title text-danger small mb-2">
                Failed
              </div>

              <div
                v-for="file in failed"
                :key="file.fileId"
                class="upload-item"
              >
                <div class="d-flex justify-content-between">
                  <span class="text-truncate text-danger">
                    <i :class="`mdi font-size-14 me-1 ${getFileIcon(file.fileName)}`"></i>
                    {{ file.fileName }}
                  </span>
                  <i class="bx bx-error-circle text-danger"></i>
                </div>

                <small v-if="file.errorMessage" class="text-danger d-block mt-1">
                  {{ file.errorMessage }}
                </small>

                <b-progress
                  :value="100"
                  height="6px"
                  class="mt-1"
                  variant="danger"
                />
              </div>
            </div>

          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex"

export default {
  computed: {
    ...mapState("aiFileManager/smartUpload", ["uploads", "isOpen", "isMinimized"]),
    ...mapGetters("aiFileManager/smartUpload", ["uploading", "completed", "failed"]),
    hasFiles() {
      return this.uploading.length > 0 || this.completed.length > 0 || this.failed.length > 0
    }
  },

  mounted() {
    this.$store.dispatch("aiFileManager/smartUpload/registerSignalR")
    this.$store.dispatch("aiFileManager/smartUpload/fetchUploads")
    window.addEventListener("beforeunload", this.onBeforeUnload)
  },

  beforeDestroy() {
    this.$store.dispatch("aiFileManager/smartUpload/unregisterSignalR")
    window.removeEventListener("beforeunload", this.onBeforeUnload)
  },

  methods: {
    onBeforeUnload(e) {
      const hasActiveUpload = this.uploads.some(u => u.status === 'uploading')
      if (hasActiveUpload) {
        e.preventDefault()
        e.returnValue = ''
      }
    },
    toggleMinimize() {
      this.$store.commit("aiFileManager/smartUpload/TOGGLE_MINIMIZE")
    },
    closePanel() {
      this.$store.commit("aiFileManager/smartUpload/CLOSE_PANEL")
    },
    getFileIcon(fileName) {
      return this.$getFileIcon(fileName)
    },
    review(item) {
      this.$root.$emit(`ai-files-upload`, item);

      this.$nextTick(() => {
        this.toggleMinimize();
      });
    }
  }
}
</script>

<style scoped>
.upload-panel {
  position: fixed;
  bottom: 25px;
  left: calc(60px + 25px);
  width: 380px;
  border-radius: 12px;
  z-index: 1055;

  display: flex;
  flex-direction: column;
  max-height: 500px; /* 👈 overall limit */
}

/* Make body flex */
.upload-body {
  flex: 1;
  overflow: hidden;
  padding: 0;
}

/* Scrollable area */
.upload-scroll {
  max-height: 380px; /* 👈 controls scroll height */
  overflow-y: auto;
  padding: 12px;
}

/* Smooth modern scrollbar */
.upload-scroll::-webkit-scrollbar {
  width: 6px;
}

.upload-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.upload-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.35);
}

.upload-item {
  padding: 8px 0;
  border-bottom: 1px solid #f1f1f1;
}

.upload-item:last-child {
  border-bottom: none;
}

/* FAB stays same */
.upload-fab {
  position: fixed;
  bottom: 25px;
  left: calc(60px + 25px);
  width: 55px;
  height: 55px;
  background: #556ee6;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 22px;
  z-index: 1060;
}

.upload-fab .badge {
  position: absolute;
  top: -5px;
  right: -5px;
}

/* animation */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Mobile responsiveness */
@media (max-width: 575.98px) {
  .upload-panel {
    left: 10px;
    right: 10px;
    bottom: 15px;
    width: auto;
    max-height: 70vh;
  }

  .upload-scroll {
    max-height: calc(70vh - 120px);
  }

  .upload-fab {
    left: 15px;
    bottom: 15px;
  }
}
</style>